import React from "react";
import { cn } from "../../utils/cn";

interface Step {
  number: number;
  title: string;
  path: string;
}

interface VerticalProgressProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}

const VerticalProgress = React.forwardRef<
  HTMLDivElement,
  VerticalProgressProps
>(({ steps, currentStep, className }, ref) => {
  return (
    <div ref={ref} className={cn("flex flex-col space-y-4", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep - 1;
        const isCurrent = index === currentStep - 1;

        return (
          <div key={index} className="flex items-start">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                  isCompleted
                    ? "bg-primary text-primary-foreground"
                    : isCurrent
                      ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {isCompleted ? "✓" : step.number}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "w-0.5 h-8 mt-2 transition-colors",
                    isCompleted ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </div>
            <div className="ml-4 flex items-center min-h-8">
              <span
                className={cn(
                  "text-sm font-medium transition-colors leading-none",
                  isCompleted
                    ? "text-primary"
                    : isCurrent
                      ? "text-primary font-semibold"
                      : "text-muted-foreground"
                )}
              >
                {step.title}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
});

VerticalProgress.displayName = "VerticalProgress";

export { VerticalProgress };
